import {
  extractCoverImageId,
  extractOgImageId,
  mapMarkdownImageUrls,
} from "@brains/image";
import { EntityUrlGenerator } from "@brains/site-composition";
import { getErrorMessage } from "@brains/utils/error";
import type { Logger } from "@brains/utils/logger";
import { pluralize } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";
import type { SiteImageLookup } from "@brains/site-engine";
import type { BaseEntity, ServiceEntityService } from "@brains/plugins";
import type { BuildPipelineContext } from "./build-pipeline-context";

const entityWithSlugSchema: z.ZodObject<
  {
    id: z.ZodString;
    entityType: z.ZodString;
    content: z.ZodString;
    metadata: z.ZodObject<{ slug: z.ZodString }, z.core.$loose>;
  },
  z.core.$loose
> = z.looseObject({
  id: z.string(),
  entityType: z.string(),
  content: z.string(),
  metadata: z.looseObject({
    slug: z.string(),
  }),
});

type EntityWithSlug = z.output<typeof entityWithSlugSchema>;

const imageEntitySchema: z.ZodObject<
  {
    content: z.ZodString;
    metadata: z.ZodObject<
      {
        width: z.ZodOptional<z.ZodNumber>;
        height: z.ZodOptional<z.ZodNumber>;
      },
      z.core.$loose
    >;
  },
  z.core.$loose
> = z.looseObject({
  content: z.string(),
  metadata: z.looseObject({
    width: z.number().optional(),
    height: z.number().optional(),
  }),
});

// Type for enriched entity with url, typeLabel, listUrl, and listLabel
export interface EnrichedEntity extends EntityWithSlug {
  url: string;
  typeLabel: string;
  listUrl: string;
  listLabel: string;
  coverImageUrl?: string | undefined;
  ogImageUrl?: string | undefined;
  coverImageWidth?: number | undefined;
  coverImageHeight?: number | undefined;
  coverImageSrcset?: string | undefined;
  coverImageSizes?: string | undefined;
}

export interface ContentEnrichmentOptions {
  pipelineContext: Pick<BuildPipelineContext, "services" | "entityDisplay">;
  imageBuildService?: SiteImageLookup | null | undefined;
  siteUrl?: string | undefined;
}

/**
 * Auto-enrich data with URL, typeLabel, and coverImageUrl fields.
 * Recursively traverses data and adds url/typeLabel/coverImageUrl to any entity objects.
 */
export async function enrichWithUrls(
  data: unknown,
  options: ContentEnrichmentOptions,
): Promise<unknown> {
  return enrichValue(
    data,
    options,
    new EntityUrlGenerator(options.pipelineContext.entityDisplay),
  );
}

async function enrichValue(
  data: unknown,
  options: ContentEnrichmentOptions,
  urlGenerator: EntityUrlGenerator,
): Promise<unknown> {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    return Promise.all(
      data.map((item) => enrichValue(item, options, urlGenerator)),
    );
  }

  if (typeof data !== "object") {
    return data;
  }

  // Recursively enrich all nested objects first (in parallel)
  const enriched: Record<string, unknown> = {};
  const entries = Object.entries(data);
  const enrichedValues = await Promise.all(
    entries.map(([, value]) => enrichValue(value, options, urlGenerator)),
  );
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry) {
      enriched[entry[0]] = enrichedValues[i];
    }
  }

  // Check if this object is an entity with slug metadata
  const entityCheck = entityWithSlugSchema.safeParse(data);
  if (!entityCheck.success) {
    return enriched;
  }

  const entity = entityCheck.data;
  const entityType = entity.entityType;
  const slug = entity.metadata.slug;

  const config = options.pipelineContext.entityDisplay?.[entityType];

  const typeLabel = config
    ? config.label
    : entityType.charAt(0).toUpperCase() + entityType.slice(1);

  // Compute listUrl and listLabel (plural) for breadcrumbs
  const pluralName = config
    ? (config.pluralName ?? config.label.toLowerCase() + "s")
    : pluralize(entityType);
  const listUrl = `/${pluralName}`;
  const listLabel = pluralName.charAt(0).toUpperCase() + pluralName.slice(1);

  // Resolve cover image: prefer pre-optimized build image, fall back to data URL
  const coverImageId = extractCoverImageId(entity);
  const coverImageFields = await resolveImageFields(coverImageId, options);

  const ogImageId = extractOgImageId(entity) ?? coverImageId;
  const ogImage = await resolveImageForHead(ogImageId, options);

  const enrichedEntity: EnrichedEntity = {
    ...enriched,
    ...entity,
    url: urlGenerator.generateUrl(entityType, slug),
    typeLabel,
    listUrl,
    listLabel,
    ...coverImageFields,
    ...(ogImage && { ogImageUrl: ogImage }),
  };

  return enrichedEntity;
}

async function resolveImageFields(
  imageId: string | undefined,
  options: ContentEnrichmentOptions,
): Promise<Partial<EnrichedEntity>> {
  const preResolved = imageId
    ? options.imageBuildService?.get(imageId)
    : undefined;
  if (preResolved) {
    return {
      coverImageUrl: preResolved.src,
      coverImageWidth: preResolved.width,
      coverImageHeight: preResolved.height,
      ...(preResolved.srcset && {
        coverImageSrcset: preResolved.srcset,
        coverImageSizes: preResolved.sizes,
      }),
    };
  }

  const image = await resolveCoverImage(
    imageId,
    options.pipelineContext.services.entityService,
  );
  if (!image) return {};
  return {
    coverImageUrl: image.url,
    ...(image.width && { coverImageWidth: image.width }),
    ...(image.height && { coverImageHeight: image.height }),
  };
}

async function resolveImageForHead(
  imageId: string | undefined,
  options: ContentEnrichmentOptions,
): Promise<string | undefined> {
  if (!imageId) return undefined;
  const preResolved = options.imageBuildService?.get(imageId);
  if (preResolved) return toAbsoluteUrl(preResolved.src, options.siteUrl);

  const image = await resolveCoverImage(
    imageId,
    options.pipelineContext.services.entityService,
  );
  if (!image) return undefined;
  // A data: URL is unusable as an og:image/twitter:image — social crawlers
  // reject it — so omit the head image rather than emit broken metadata. The
  // pre-resolved branch above returns a real optimized file URL.
  if (image.url.startsWith("data:")) return undefined;
  return toAbsoluteUrl(image.url, options.siteUrl);
}

function toAbsoluteUrl(url: string, siteUrl: string | undefined): string {
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  if (!siteUrl) return url;
  return `${siteUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

async function resolveCoverImage(
  imageId: string | undefined,
  entityService: ServiceEntityService,
): Promise<
  | {
      url: string;
      width?: number;
      height?: number;
    }
  | undefined
> {
  if (!imageId) return undefined;

  const image = await entityService.getEntity({
    entityType: "image",
    id: imageId,
  });
  const imageCheck = imageEntitySchema.safeParse(image);
  if (!imageCheck.success) return undefined;

  return {
    url: imageCheck.data.content,
    ...(imageCheck.data.metadata.width && {
      width: imageCheck.data.metadata.width,
    }),
    ...(imageCheck.data.metadata.height && {
      height: imageCheck.data.metadata.height,
    }),
  };
}

/**
 * Discover rendered Markdown images (inline and reference-style) and
 * cover/OG references to prepare before rendering, without loading image
 * entities. Images in code and ordinary links are not rendered, so not
 * prepared.
 */
export async function collectAllImageIds(
  entityService: ServiceEntityService,
  logger: Logger,
): Promise<string[]> {
  const imageIds = new Set<string>();

  try {
    // Get all entity types that have been registered
    const entityTypes = entityService.getEntityTypes();

    for (const entityType of entityTypes) {
      if (entityType === "image") continue; // Skip image entities themselves

      await forEachEntityPage(entityService, entityType, (entities) =>
        entities.forEach((entity) => {
          const coverImageId = extractCoverImageId(entity);
          if (coverImageId) {
            imageIds.add(coverImageId);
          }
          const ogImageId = extractOgImageId(entity);
          if (ogImageId) {
            imageIds.add(ogImageId);
          }
          if (entity.content.includes("entity://image/")) {
            mapMarkdownImageUrls(entity.content, (url) => {
              const match = /^entity:\/\/image\/(.+)$/.exec(url);
              if (match?.[1]) imageIds.add(match[1]);
              return url;
            });
          }
        }),
      );
    }
  } catch (error) {
    logger.warn("Failed to collect image IDs for pre-resolution", {
      error: getErrorMessage(error),
    });
  }

  return [...imageIds];
}

/** Entities a scan holds at once: a book brain has tens of thousands. */
const SCAN_PAGE_SIZE = 1000;

/** Visit every entity of a type, one page at a time, in stable id order. */
async function forEachEntityPage(
  entityService: ServiceEntityService,
  entityType: string,
  visit: (entities: BaseEntity[]) => void,
  offset = 0,
): Promise<void> {
  const page = await entityService.listEntities({
    entityType,
    options: {
      limit: SCAN_PAGE_SIZE,
      offset,
      sortFields: [{ field: "id", direction: "asc" }],
    },
  });
  visit(page);
  if (page.length < SCAN_PAGE_SIZE) return;
  await forEachEntityPage(
    entityService,
    entityType,
    visit,
    offset + page.length,
  );
}
